import { RefreshIcon } from "@heroicons/react/outline";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getData, OAuthError, postData } from "../backend";
import { useModal } from "../contexts";
import useUser from "../hooks/useUser";
import LoadingModal from "./LoadingModal";
import OAuthModal from "./OAuthModal";
import SettingsModalHubspot from "./SettingsModalHubspot";
import SettingsModalMicrosoft from "./SettingsModalMicrosoft";
import SettingsModalStripe from "./SettingsModalStripe";
import { User } from "../interfaces";
import jwtDecode from "jwt-decode";

const SHAREPOINT_SCOPE = "Sites.ReadWrite.All";

export type IntegrationSettings = {
  title: string;
  description: string;
  onConnectClick: (callback?: () => void) => void;
  onManageClick: (callback?: () => void) => void;
  isBeta?: boolean;
}

function SettingsPermissions() {
  const { t } = useTranslation();
  const { openModal, closeModal } = useModal();
  const { user, hasPermission } = useUser({ revalidateOnFocus: false });

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [shouldRefreshOnFocus, setShouldRefreshOnFocus] = useState(false);

  // Google
  const [scopes, setScopes] = useState<Array<string>>([]);
  const isGmailConnected = scopes.includes("https://www.googleapis.com/auth/gmail.send");
  const isDriveLimited = scopes.includes("https://www.googleapis.com/auth/drive.file");
  const isDriveFull = scopes.includes("https://www.googleapis.com/auth/drive");

  // Individual
  const [isMicrosoftConnected, setIsMicrosoftConnected] = useState(false);
  const [isSharepointConnected, setIsSharepointConnected] = useState(false);

  const isStripeConnected = !!user?.stripeConnectedAccount?.accountCompleted;

  // Teams Hubspot
  const [hubspotTeamsCredentials, setHubspotTeamsCredentials] = useState<{ portalId?: string, user?: User, appObjectsEnabled?: boolean }>();
  const isHubspotTeamsConnected = !!hubspotTeamsCredentials?.portalId;

  const fetchPermissions = useCallback(async () => {
    setIsRefreshing(true);

    await getData("/auth/scopes/")
      .then(data => setScopes(data.scopes))
      .catch(() => setScopes([]));

    await postData("/microsoft/access-token/")
      .then((token) => {
        setIsMicrosoftConnected(true);

        try {
          const payload: Record<string, string> = jwtDecode(token);
          const scopes = payload["scp"]

          setIsSharepointConnected(scopes.includes(SHAREPOINT_SCOPE));
        }
        catch {
          setIsSharepointConnected(false);
        }
      })
      .catch(() => { setIsMicrosoftConnected(false); setIsSharepointConnected(false) });

    await getData("/hubspot/teams-credentials/")
      .then((data) => setHubspotTeamsCredentials(data))
      .catch(() => setHubspotTeamsCredentials(undefined));

    setIsRefreshing(false);
  }, []);

  const fetchPermissionsOnFocus = useCallback(async () => {
    if (!shouldRefreshOnFocus)
      return

    setShouldRefreshOnFocus(false);
    fetchPermissions();

  }, [fetchPermissions, shouldRefreshOnFocus]);

  useEffect(() => {
    fetchPermissions();

    window.addEventListener("focus", fetchPermissionsOnFocus);
    return () => window.removeEventListener("focus", fetchPermissionsOnFocus);

  }, [fetchPermissions, fetchPermissionsOnFocus]);

  async function connectToHubspot() {
    openModal(<LoadingModal />)

    const { url } = await postData("/hubspot/install/");
    window.open(url, "_blank");

    closeModal();
    setShouldRefreshOnFocus(true);
  }

  async function connectToMicrosoft(scopes: Array<string> = []) {
    openModal(<LoadingModal />)

    const { url } = await postData("/microsoft/install/", { scopes });
    window.open(url, "_blank");

    closeModal();
    setShouldRefreshOnFocus(true);
  }

  async function handleAuthoriseScope(scope: string) {
    openModal(<LoadingModal />)

    await postData("/auth/access-token/", { scopes: [scope] })
      .catch(error => {
        if (error instanceof OAuthError)
          openModal(<OAuthModal oauthUrl={error.oauthUrl} onClose={() => fetchPermissions()} />);
        else
          console.error(error)
      });

    setShouldRefreshOnFocus(true);
  }

  return (
    <div className="flex flex-col p-8 overflow-y-auto w-full">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="font-gilroy font-semibold text-lg">
            {t("Integrations and Permissions")}
          </h3>
          <p className="text-gray-400 mb-8">
            {t("Manage services of which you have provided Portant access to")}
          </p>
        </div>
        <RefreshIcon className={`w-6 h-6 text-gray-400 hover:text-gray-600 cursor-pointer ${isRefreshing ? "animate-spin" : "animate-none"}`} onClick={() => fetchPermissions()} />
      </div>

      <div className="flex flex-col gap-8 w-full">
        {user?.activeTeam &&
          <div className="font-gilroy font-semibold -mb-6">{t("Individual Permissions")}</div>
        }

        {/* Gmail */}
        <div className="flex items-center gap-4">
          <img src={`${process.env.REACT_APP_STATIC_URL}/gmail-icon.svg`} className="w-8 h-8 flex-shrink-0" alt="Gmail Icon" />
          <div className="flex flex-col">
            <div className="flex gap-2">
              <div className="font-semibold font-gilroy text-black">Gmail</div>
              {isGmailConnected &&
                <div className="font-semibold text-green">Connected</div>
              }
            </div>
            <div>{t("Allow Portant to send emails on your behalf.")}</div>
          </div>
          {isGmailConnected
            ? <button className="btn btn-white w-32 ml-auto" disabled={isRefreshing} onClick={() => window.open("https://myaccount.google.com/permissions", "_blank")}>{t("Manage")}</button>
            : <button className="btn btn-blue w-32 ml-auto" disabled={isRefreshing} onClick={() => handleAuthoriseScope("https://www.googleapis.com/auth/gmail.send")}>{t("Connect")}</button>
          }
        </div>

        {/* Google Drive */}
        <div className="flex items-center gap-4">
          <img src={`${process.env.REACT_APP_STATIC_URL}/drive-icon.svg`} className="w-8 h-8 flex-shrink-0" alt="Gmail Icon" />
          <div className="flex flex-col">
            <div className="flex gap-2">
              <div className="font-semibold font-gilroy text-black">Google Drive</div>
              {isDriveFull &&
                <div className="font-semibold text-green">Connected</div>
              }
              {isDriveLimited &&
                <div className="font-semibold text-yellow">Limited</div>
              }
            </div>
            <div>{t("Allow Portant to access and create files in your Google Drive.")}</div>
          </div>
          {isDriveFull
            ? <button className="btn btn-white w-32 ml-auto" disabled={isRefreshing} onClick={() => window.open("https://myaccount.google.com/permissions", "_blank")}>{t("Manage")}</button>
            : <button className="btn btn-blue w-32 ml-auto" disabled={isRefreshing} onClick={() => handleAuthoriseScope("https://www.googleapis.com/auth/drive")}>{t("Connect")}</button>
          }
        </div>

        {/* Microsoft */}
        <div className="flex items-center gap-4">
          <img src={`${process.env.REACT_APP_STATIC_URL}/microsoft-icon.svg`} className="w-8 h-8 flex-shrink-0" alt="Microsoft Icon" />
          <div className="flex flex-col">
            <div className="flex gap-2">
              <div className="font-semibold font-gilroy text-black">Microsoft</div>
              {isMicrosoftConnected &&
                <div className="font-semibold text-green">Connected</div>
              }
            </div>
            <div>{t("Allow Portant to access your OneDrive and Outlook accounts.")}</div>
          </div>
          {isMicrosoftConnected
            ? <button className="btn btn-white w-32 ml-auto" disabled={isRefreshing} onClick={() => openModal(<SettingsModalMicrosoft onDisconnect={fetchPermissions} />)}>{t("Manage")}</button>
            : <button className="btn btn-blue w-32 ml-auto" disabled={isRefreshing} onClick={() => connectToMicrosoft()}> {t("Connect")}</button>
          }
        </div>

        {user?.activeTeam &&
          <div className="font-gilroy font-semibold -mb-6">{t("Teams Permissions")}</div>
        }

        {/* HubSpot (Teams) */}
        <div className="flex items-center gap-4">
          <img src={`${process.env.REACT_APP_STATIC_URL}/hubspot-icon.svg`} className="w-8 h-8 flex-shrink-0" alt="HubSpot Icon" />
          <div className="flex flex-col">
            <div className="flex gap-2">
              <div className="font-semibold font-gilroy text-black">HubSpot</div>
              {isHubspotTeamsConnected &&
                <div className="flex items-baseline">
                  <div className="font-semibold text-green">Connected</div>
                  {hubspotTeamsCredentials.user &&
                    <div className="font-semibold text-xs text-gray-400 ml-1">by {hubspotTeamsCredentials.user.fullName} ({hubspotTeamsCredentials.user.email})</div>
                  }
                </div>
              }
            </div>
            {hubspotTeamsCredentials?.portalId
              ? <div>{t("Your team is connected to portal ID:")} <a className="font-semibold text-gray-600 underline" target="_blank" rel="noreferrer" href={`https://app.hubspot.com/reports-dashboard/${hubspotTeamsCredentials?.portalId}`}>{hubspotTeamsCredentials?.portalId}</a></div>
              : <div>{t("Allow Portant to access HubSpot for your whole team.")}</div>
            }
          </div>
          {isHubspotTeamsConnected
            ? <button className="btn btn-white w-32 ml-auto" disabled={isRefreshing || !hasPermission(user?.activeTeam, "teams.admin")} onClick={() => openModal(<SettingsModalHubspot appObjectsEnabled={!!hubspotTeamsCredentials?.appObjectsEnabled} onDisconnect={fetchPermissions} onReauthorise={() => setShouldRefreshOnFocus(true)} />)}>{t("Manage")}</button>
            : <button className="btn btn-blue w-32 ml-auto" disabled={isRefreshing || !hasPermission(user?.activeTeam, "teams.admin")} onClick={() => connectToHubspot()}>{t("Connect")}</button>
          }
        </div>

        <div className="flex items-center gap-4">
          <img src={`${process.env.REACT_APP_STATIC_URL}/sharepoint-icon.svg`} className="w-8 h-8 flex-shrink-0" alt="Sharepoint Icon" />
          <div className="flex flex-col">
            <div className="flex gap-2">
              <div className="font-semibold font-gilroy text-black">Microsoft SharePoint</div>
              {isSharepointConnected &&
                <div className="font-semibold text-green">Connected</div>
              }
            </div>
            <div>{t("Allow Portant to access your organisation's SharePoint account.")}</div>
          </div>
          {isSharepointConnected
            ? <button className="btn btn-white w-32 ml-auto" disabled={isRefreshing} onClick={() => openModal(<SettingsModalMicrosoft onDisconnect={fetchPermissions} />)}>{t("Manage")}</button>
            : <button className="btn btn-blue w-32 ml-auto" disabled={isRefreshing} onClick={() => connectToMicrosoft([SHAREPOINT_SCOPE])}> {t("Connect")}</button>
          }
        </div>

        {/* Stripe */}
        <div className="flex items-center gap-4">
          <img src={`${process.env.REACT_APP_STATIC_URL}/stripe-logo.png`} className="w-8 h-8 flex-shrink-0 rounded" alt="Stripe Icon" />
          <div className="flex flex-col">
            <div className="flex gap-2">
              <div className="font-semibold font-gilroy text-black">Stripe</div>
              {isStripeConnected &&
                <div className="font-semibold text-green">Connected</div>
              }
            </div>
            <div>{t("Allow Portant to collect payments on behalf via Stripe Connect.")}</div>
          </div>
          {isStripeConnected
            ? <button className="btn btn-white w-32 ml-auto" disabled={isRefreshing || !hasPermission(user?.activeTeam, "teams.admin")} onClick={() => openModal(<SettingsModalStripe onDisconnect={fetchPermissions} />)}>{t("Manage")}</button>
            : <button className="btn btn-blue w-32 ml-auto" disabled={isRefreshing || !hasPermission(user?.activeTeam, "teams.admin")} onClick={() => openModal(<SettingsModalStripe onDisconnect={fetchPermissions} />)}>{t("Connect")}</button>
          }
        </div>
      </div>
    </div>
  );
}

export default SettingsPermissions;
